import { Ecosystem, Maybe, PackageManager, VulnerabilitySeverity } from '~/types/types'

export function getEcosystemIcon(eco: Ecosystem) {
  const iconMap: Record<Ecosystem, string> = {
    [Ecosystem.Almalinux]: 'javascript',
    [Ecosystem.Alpine]: 'javascript',
    [Ecosystem.Android]: 'javascript',
    [Ecosystem.Bioconductor]: 'javascript',
    [Ecosystem.Bitnami]: 'javascript',
    [Ecosystem.ConanCenter]: 'javascript',
    [Ecosystem.Cran]: 'javascript',
    [Ecosystem.CratesIo]: 'rust',
    [Ecosystem.Debian]: 'javascript',
    [Ecosystem.Ghc]: 'javascript',
    [Ecosystem.GithubActions]: 'javascript',
    [Ecosystem.Go]: 'go',
    [Ecosystem.Hackage]: 'javascript',
    [Ecosystem.Hex]: 'javascript',
    [Ecosystem.Linux]: 'javascript',
    [Ecosystem.Maven]: 'javascript',
    [Ecosystem.Npm]: 'javascript',
    [Ecosystem.Nuget]: 'csharp',
    [Ecosystem.OssFuzz]: 'javascript',
    [Ecosystem.Packagist]: 'php',
    [Ecosystem.PhotonOs]: 'javascript',
    [Ecosystem.Pub]: 'javascript',
    [Ecosystem.Pypi]: 'python',
    [Ecosystem.RockyLinux]: 'javascript',
    [Ecosystem.Rubygems]: 'ruby',
    [Ecosystem.Swift]: 'swift',
    [Ecosystem.Ubuntu]: 'javascript'
  }

  return iconMap[eco]
}

export function getEcosystemName(eco: Ecosystem) {
  const nameMap: Record<Ecosystem, string> = {
    [Ecosystem.Almalinux]: 'AlmaLinux',
    [Ecosystem.Alpine]: 'Alpine',
    [Ecosystem.Android]: 'Android',
    [Ecosystem.Bioconductor]: 'Bioconductor',
    [Ecosystem.Bitnami]: 'Bitnami',
    [Ecosystem.ConanCenter]: 'ConanCenter',
    [Ecosystem.Cran]: 'CRAN',
    [Ecosystem.CratesIo]: 'crates.io',
    [Ecosystem.Debian]: 'Debian',
    [Ecosystem.Ghc]: 'GHC',
    [Ecosystem.GithubActions]: 'Github Actions',
    [Ecosystem.Go]: 'Go',
    [Ecosystem.Hackage]: 'Hackage',
    [Ecosystem.Hex]: 'Hex',
    [Ecosystem.Linux]: 'Linux',
    [Ecosystem.Maven]: 'Maven',
    [Ecosystem.Npm]: 'npm',
    [Ecosystem.Nuget]: 'NuGet',
    [Ecosystem.OssFuzz]: 'OSS-Fuzz',
    [Ecosystem.Packagist]: 'Packagist',
    [Ecosystem.PhotonOs]: 'Photon OS',
    [Ecosystem.Pub]: 'Pub',
    [Ecosystem.Pypi]: 'PyPI',
    [Ecosystem.RockyLinux]: 'Rocky Linux',
    [Ecosystem.Rubygems]: 'RubyGems',
    [Ecosystem.Swift]: 'Swift',
    [Ecosystem.Ubuntu]: 'Ubuntu'
  }

  return nameMap[eco]
}

export function getPackageManagerIcon(pm: PackageManager) {
  const iconMap: Record<PackageManager, string> = {
    [PackageManager.RequirementsTxt]: 'python',
    [PackageManager.Poetry]: 'python',
    [PackageManager.Pipfile]: 'python',
    [PackageManager.Pdm]: 'python',
    [PackageManager.Npm]: 'javascript',
    [PackageManager.Yarn]: 'javascript',
    [PackageManager.Pnpm]: 'javascript',
    [PackageManager.Gradle]: 'java',
    [PackageManager.Maven]: 'java',
    [PackageManager.GoMod]: 'go',
    [PackageManager.RubyGems]: 'ruby',
    [PackageManager.Nuget]: 'csharp',
    [PackageManager.Packagist]: 'php',
    [PackageManager.Cargo]: 'rust',
    [PackageManager.Bun]: 'javascript',
    [PackageManager.Uv]: 'python'
  }
  return iconMap[pm]
}

export function getPackageManagerName(pm: PackageManager) {
  const nameMap: Record<PackageManager, string> = {
    [PackageManager.RequirementsTxt]: 'requirements.txt (Python)',
    [PackageManager.Poetry]: 'Poetry (Python)',
    [PackageManager.Pipfile]: 'Pipfile (Python)',
    [PackageManager.Pdm]: 'PDM (Python)',
    [PackageManager.Npm]: 'NPM (JavaScript)',
    [PackageManager.Yarn]: 'Yarn (JavaScript)',
    [PackageManager.Pnpm]: 'PNPM (JavaScript)',
    [PackageManager.Gradle]: 'Gradle (Java)',
    [PackageManager.Maven]: 'Maven (Java)',
    [PackageManager.GoMod]: 'Go Mod (Go)',
    [PackageManager.RubyGems]: 'Ruby Gems (Ruby)',
    [PackageManager.Nuget]: 'Nuget (C#)',
    [PackageManager.Packagist]: 'Packagist (PHP)',
    [PackageManager.Cargo]: 'Cargo (Rust)',
    [PackageManager.Bun]: 'Bun (JavaScript)',
    [PackageManager.Uv]: 'UV (Python)'
  }
  return nameMap[pm]
}

export const severityIconMap = {
  [VulnerabilitySeverity.Critical]: { icon: 'severity-critical', color: 'cherry-500' },
  [VulnerabilitySeverity.High]: { icon: 'severity-high', color: 'amber' },
  [VulnerabilitySeverity.Medium]: { icon: 'severity-medium', color: 'honey' },
  [VulnerabilitySeverity.Low]: { icon: 'severity-low', color: 'vanilla-400' },
  [VulnerabilitySeverity.None]: { icon: 'severity-low', color: 'vanilla-400' }
}

export const severityIconModifiedByAIMap = {
  [VulnerabilitySeverity.Critical]: { icon: 'severity-critical-ai', color: 'cherry-500' },
  [VulnerabilitySeverity.High]: { icon: 'severity-high-ai', color: 'amber' },
  [VulnerabilitySeverity.Medium]: { icon: 'severity-medium-ai', color: 'honey' },
  [VulnerabilitySeverity.Low]: { icon: 'severity-low-ai', color: 'vanilla-400' },
  [VulnerabilitySeverity.None]: { icon: 'severity-low-ai', color: 'vanilla-400' }
}

export const severityLabelTypeMap = {
  [VulnerabilitySeverity.Critical]: 'cherry',
  [VulnerabilitySeverity.High]: 'amber',
  [VulnerabilitySeverity.Medium]: 'honey',
  [VulnerabilitySeverity.Low]: 'neutral',
  [VulnerabilitySeverity.None]: 'neutral',
  UNKNOWN: 'neutral'
}

export function getDirectoryAndFileFromPath(path?: Maybe<string>): {
  directory: string
  file: string
} {
  if (!path) return { directory: '', file: '' }
  const segments = path.split('/')
  const file = segments.pop() || ''
  const directory = segments.join('/')
  return { directory, file }
}

export function getTargetTitle(manifestPath?: Maybe<string>, lockfilePath?: Maybe<string>) {
  const { directory: manifestDirectory, file: manifestFilename } =
    getDirectoryAndFileFromPath(manifestPath)
  const { directory: lockfileDirectory, file: lockfileFilename } =
    getDirectoryAndFileFromPath(lockfilePath)
  return {
    manifestDirectory,
    manifestFilename,
    lockfileDirectory,
    lockfileFilename
  }
}
